import {Component, computed, EventEmitter, inject, input, Output, TrackByFunction} from '@angular/core';
import {Maybe, Rating, WineProduct} from '@core/interfaces/generated/graphql';
import {
  ProductRatingComponent
} from '@modules/page/components/product/components/product-ratings-section/product-rating/product-rating.component';
import {NguCarousel, NguCarouselConfig, NguCarouselDefDirective, NguTileComponent} from '@ngu/carousel';
import {CarouselPointComponent} from '@layout/components/general/carousel-point/carousel-point.component';
import {GridService} from '@core/services/grid.service';

@Component({
  selector: 'app-product-ratings-section',
  templateUrl: './product-ratings-section.component.html',
  imports: [
    ProductRatingComponent,
    NguCarousel,
    NguTileComponent,
    NguCarouselDefDirective,
    CarouselPointComponent
  ],
  styleUrl: './product-ratings-section.component.scss'
})
export class ProductRatingsSectionComponent {

  gridService = inject(GridService)

  product = input.required<WineProduct>();

  @Output() toggleReadMore = new EventEmitter<boolean>();

  public carouselTile: NguCarouselConfig = {
    grid: {
      xs: 1, sm: 1, md: 1, lg: 1, xl: 1, all: 0
    },
    slide: 1,
    speed: 350,
    point: {
      visible: true
    },
    load: 2,
    velocity: 0,
    loop: true,
    touch: true,
    easing: 'cubic-bezier(0, 0, 0.2, 1)'
  };

  // Sort ratings so current vintage reviews come first, previous vintage reviews come last
  // Show ratings that have meaningful content: review, label, or points
  sortedRatings = computed(() => {
    const ratings = this.product().meta?.ratings;
    if (!ratings) {
      return [];
    }

    // Keep ratings that have meaningful content (review, label, or points)
    const ratingsWithContent = ratings.filter(rating => {
      // Show if it has a review
      if (rating?.review && rating.review.trim().length > 0) {
        return true;
      }

      // Show if it has a label
      if (rating?.label && rating.label.trim().length > 0) {
        return true;
      }

      // Show if it has rating points
      if (rating?.rating && rating.rating > 0) {
        return true;
      }

      return false;
    });

    // Create a copy and sort: current vintage (isPreviousVintage is falsy) first, previous vintage (isPreviousVintage is true) last
    return [...ratingsWithContent].sort((a, b) => {
      const aIsPrevious = !!a?.isPreviousVintage;
      const bIsPrevious = !!b?.isPreviousVintage;

      // If both are same type (both current or both previous), maintain original order
      if (aIsPrevious === bIsPrevious) {
        return 0;
      }

      // Current vintage (false) comes before previous vintage (true)
      return aIsPrevious ? 1 : -1;
    });
  });

  trackBy(): TrackByFunction<Maybe<Rating>> {
    return (index, item) => {
      return item?.id;
    };
  }

}
